import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Divider, Radio, Table, TableColumnsType, Tag, Typography} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined, UserAddOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import confirmAction from '../../helpers/confirmAction';
import {messageType, showMessages} from '../../helpers/message';
import {ucFirst} from '../../helpers/misc';
import {clearActiveMemberStaff} from '../../store/slices/userSlice';
import {dateFormat} from '../../lib/time';
import {useAppSelector} from '../../hooks/useStore';
import {
    useRejoinMembershipMutation,
    useEndMembershipMutation,
    useGetAccountProfilesRolesPaginatedQuery
} from '../../store/api/accountApi';
import {useOneOwnerRole} from '../../hooks/useOneOwnerRole';
import {useAppDispatch} from '../../hooks/useStore';
import {selectCurrentProfile} from '../../store/slices/userSlice';
import {CurrentActionComponentParams, ProfileDriver, RemovedActionComponentParams} from '../../types/accountTypes';
import {BaseError} from '../../types/apiTypes';
import {getRoleColor} from '../../helpers/account';
import AccountRoleManagementDrawer from '../../containers/staff/accounts/AccountRoleManagementDrawer';

import './AccountDriverManagementTable.less';

// Component for "Rejoin" button.
const RemovedAction = ({row, addPerson}: RemovedActionComponentParams) => {
    return (
        <Button
            className="account-users-table-actions-btn"
            type="link"
            key="rejoin-button"
            icon={<UserAddOutlined/>}
            onClick={() => {
                confirmAction(
                    'Are you sure you want to add this person back to the account?',
                    'This actions re-establishes this person\'s membership with the account.' +
                    ' They will maintain the previous role(s) they were assigned, but you may edit their role(s)' +
                    ' after they rejoin.',
                    addPerson,
                    row.id
                );
            }}
        >
        </Button>
    );
};

// Action component for "Edit" and "Remove".
const CurrentAction = ({
    row,
    setSelectedProfile,
    setIsDrawerOpen,
    removePerson
}: CurrentActionComponentParams) => {
    return (
        <div className="account-manage-action-btn-container">
            <Button
                className="account-users-table-actions-btn"
                type="link"
                key="edit-button"
                icon={<EditOutlined/>}
                onClick={() => {
                    setSelectedProfile(row);
                    setIsDrawerOpen(true);
                }}
            />
            <Divider type="vertical"/>
            <Button
                className="account-users-table-actions-btn"
                type="link"
                key="delete-button"
                icon={<DeleteOutlined/>}
                onClick={() => {
                    confirmAction(
                        'Are you sure you want to remove this person from your account?',
                        'This action removes this person\'s membership with your account.' +
                        ' It does not delete any personal data.',
                        () => removePerson(row)
                    );
                }}
            />
        </div>
    );
};


// Columns for when the filter is set to inactive, displaying the start and end dates of last activity.
const lastActiveColumn = [
    {
        title: <Typography.Text strong>Last Active</Typography.Text>,
        dataIndex: 'undefined',
        key: 'last_active',
        render: (_: any, profile: ProfileDriver) => {
            const startDate = profile ? dayjs(profile.started_at).tz().format(dateFormat) : '';
            const endDate = profile ? dayjs(profile.started_at).tz().format(dateFormat) : 'Ongoing';

            return <span className="last-active-line-item">{`${startDate} - ${endDate}`}</span>;
        },
    }
];

const AccountDriverManagementTable = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const compressRows = window.innerWidth <= 950;

    // Switching between active and inactive persons on the table.
    const [filterTableActive, setFilterTableActive] = React.useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [selectedProfile, setSelectedProfile] = React.useState<ProfileDriver | null>(null);

    const accountID = useAppSelector(selectCurrentProfile)?.account?.id;
    const accountNumber = useAppSelector(selectCurrentProfile)?.account?.account_number;
    const activeMembershipNumber = useAppSelector(state => state.user.membership)?.membership_number;

    const {oneOwnerRole} = useOneOwnerRole(accountID);
    const {data: profileData, error, isFetching: isProfileFetching} = useGetAccountProfilesRolesPaginatedQuery({
        page,
        perPage,
        accountID,
        activeMemberships: filterTableActive ? 1 : 0
    });

    const [endMembership, {
        error: endMembershipError,
        isLoading: isEndMembershipLoading,
        isSuccess: isEndMembershipSuccess,
        reset: resetEndMembership
    }] = useEndMembershipMutation();

    const [rejoinMembership, {
        error: rejoinMembershipError,
        isLoading: isRejoinMembershipLoading,
        isSuccess: isRejoinMembershipSuccess,
        reset: resetRejoinMembership
    }] = useRejoinMembershipMutation();

    React.useEffect(() => {
        if (error || endMembershipError || rejoinMembershipError) {
            showMessages(((error || endMembershipError || rejoinMembershipError) as BaseError).data?.message, messageType.error);

            resetEndMembership();
            resetRejoinMembership();
        }
    }, [error, rejoinMembershipError, endMembershipError, resetEndMembership, resetRejoinMembership]);

    React.useEffect(() => {
        if (isEndMembershipSuccess || isRejoinMembershipSuccess) {

            if (isEndMembershipSuccess) {
                showMessages('Person has been removed from account.', messageType.success);
            }

            if (isRejoinMembershipSuccess) {
                showMessages('Person successfully rejoined the account.', messageType.success);
            }

            resetEndMembership();
            resetRejoinMembership();
        }
    }, [isEndMembershipSuccess, isRejoinMembershipSuccess, resetEndMembership, resetRejoinMembership]);

    const removePerson = React.useCallback((profile: ProfileDriver) => {
        const isOwner = profile?.roles?.includes('user.owner');
        const isApplicant = profile?.roles?.includes('user.applicant');

        if (isApplicant) {
            showMessages('Applicant cannot be removed from account.', messageType.error);
            return;
        }

        if (oneOwnerRole && isOwner) {
            showMessages('You must have at least one owner role on the account.', messageType.warn);
            return;
        }

        if (profile.membership_id) {
            endMembership({membershipID: profile.membership_id});

            if (activeMembershipNumber === profile.membership_number) {
                showMessages('The member you are representing no longer has an active membership.', messageType.warn);
                dispatch(clearActiveMemberStaff());
                navigate('/member_change');
            }
        }
    }, [activeMembershipNumber, dispatch, endMembership, navigate, oneOwnerRole]);

    const addPerson = React.useCallback((profileID: number) => {
        const startedAt = dayjs().tz().format('YYYY-MM-DD HH:mm:ssZZ');
        rejoinMembership({profileID, accountID, startedAt});
    }, [accountID, rejoinMembership]);

    const onChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    };

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: profileData?.meta.total,
        onChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    };

    const activeColumns: TableColumnsType<ProfileDriver> = [
        {
            title: <Typography.Text strong>Name</Typography.Text>,
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <span className="name-line-item long-text-wrap">{name}</span>,
        },
        {
            title: <Typography.Text strong>Membership Number</Typography.Text>,
            dataIndex: 'membership_number',
            key: 'membership_number',
            width: '15%',
            render: (membership_number: string) => (
                <span className="membership-number-line-item long-text-wrap">
                    {membership_number ? accountNumber + '-' + membership_number : 'N/A'}
                </span>
            ),
        },
        {
            title: <Typography.Text strong>Email</Typography.Text>,
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => <span className="email-line-item long-text-wrap">{email}</span>,
        },
        {
            title: <Typography.Text strong>Status</Typography.Text>,
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => <span className="driver-status-line-item">{status}</span>,
        },
        {
            title: <Typography.Text strong>Roles</Typography.Text>,
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: string[]) => (
                <>
                    <span className="roles-line-item">Roles</span>
                    {roles.map(role => {
                        const color = getRoleColor(role);
                        return (
                            <Tag color={color} key={role}>
                                {ucFirst(role.slice(role.indexOf('.') + 1))}
                            </Tag>
                        );
                    })}
                </>
            ),
        },
        {
            title: <Typography.Text strong>{filterTableActive ? 'Actions' : 'Action'}</Typography.Text>,
            dataIndex: 'actions',
            key: 'actions',
            render: (_: string[], row: ProfileDriver) => (
                <>
                    <span className="actions-line-item">Actions:</span>
                    {filterTableActive ? (
                        <CurrentAction
                            row={row}
                            setSelectedProfile={setSelectedProfile}
                            setIsDrawerOpen={setIsDrawerOpen}
                            removePerson={removePerson}
                        />
                    ) : (
                        <RemovedAction
                            row={row}
                            addPerson={addPerson}
                        />
                    )}
                </>
            ),
        }
    ];
    const expandedRowRender = (record: ProfileDriver) => {
        const expandRows = [
            <li key={1} className="account-manage-expanded-row-list-item">
                <Typography.Text strong>Membership
                    Number: </Typography.Text>
                {record.membership_number ? `${accountNumber}-${record.membership_number}` : 'N/A'}
            </li>,
            <li key={2} className="account-manage-expanded-row-list-item">
                <Typography.Text className="account-manage-expanded-row-list-title" strong>Email: </Typography.Text>
                {record.email}
            </li>,
            <li key={3} className="account-manage-expanded-row-list-item">
                <Typography.Text className="account-manage-expanded-row-list-title" strong>Roles: </Typography.Text>
                {record?.roles?.map(role => {
                    const color = getRoleColor(role);
                    return (
                        <Tag color={color} key={role}>
                            {ucFirst(role.slice(role.indexOf('.') + 1))}
                        </Tag>
                    );
                })}
            </li>,
            <li key={4} className="account-manage-expanded-row-list-item">
                <Typography.Text className="account-manage-expanded-row-list-title" strong>Status: </Typography.Text>
                {record.status || 'N/A'}
            </li>,
            <li key={5} className="account-manage-expanded-action-buttons-row">
                <Typography.Text className="account-manage-expanded-row-list-title" strong>Action: </Typography.Text>
                {filterTableActive ? CurrentAction({
                    row: record,
                    setSelectedProfile,
                    setIsDrawerOpen,
                    removePerson
                }) : RemovedAction({row: record, addPerson})}
            </li>,
        ];

        return (
            <ul className="account-manage-expanded-row-list">
                {expandRows}
            </ul>
        );
    };

    // Columns for when the filter is set to inactive. We are currently not showing the Status column here.
    let inactiveColumns = activeColumns.slice(0, 2);
    inactiveColumns.push(...activeColumns.slice(2, 3), ...lastActiveColumn, ...activeColumns.slice(4, 6));
    inactiveColumns = compressRows ? inactiveColumns.slice(0, 1) : inactiveColumns;

    const isLoading = isProfileFetching || isEndMembershipLoading || isRejoinMembershipLoading;

    return (
        <>
            <Table
                rowKey="id"
                dataSource={isLoading ? [] : profileData?.data}
                loading={isLoading}
                expandable={{expandedRowRender: compressRows ? expandedRowRender : undefined}}
                columns={filterTableActive ? (compressRows ? activeColumns.slice(0, 1) : activeColumns) : inactiveColumns}
                bordered={false}
                title={() => (
                    <div className="account-users-table-title">
                        <div>
                            <Typography.Text>Account Users</Typography.Text>
                            <Typography.Text className="account-users-table-title-description">
                                {filterTableActive ? `These users are currently under the account.` : `These users were removed from the account.`}
                            </Typography.Text>
                        </div>
                        <div className="account-users-table-btn-container">
                            <Radio.Group className="account-users-table-filter-btn-container" value={filterTableActive}
                                         onChange={() => setFilterTableActive(prevState => !prevState)}>
                                <Radio.Button value={true}>Current</Radio.Button>
                                <Radio.Button value={false}>Removed</Radio.Button>
                            </Radio.Group>
                            <Button
                                className="account-users-table-add-btn"
                                type="primary"
                                icon={<PlusOutlined/>}
                                onClick={() => navigate('/account/add_person')}
                            >
                                Add New
                            </Button>
                        </div>
                    </div>
                )}
                className="account-users-table responsive-table"
                pagination={paginationAttr}
            />

            {selectedProfile && (
                <AccountRoleManagementDrawer
                    profile={selectedProfile}
                    visible={isDrawerOpen}
                    accountID={accountID}
                    onClose={() => setIsDrawerOpen(false)}
                    oneOwnerRole={oneOwnerRole}
                />
            )}
        </>
    );
};

export default AccountDriverManagementTable;
